import React, { useState } from 'react';
import {
  Listbox, ListboxButton, ListboxOptions, ListboxOption,
  Combobox, ComboboxInput, ComboboxButton, ComboboxOptions, ComboboxOption,
} from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';

// Headless UI dropdown that matches the app's dark/blue mono field style.
// options: [{ value, label }]; onChange receives the selected value.
// Long lists (>8 options) become a searchable Combobox automatically; pass `searchable` to force it.
export default function UiSelect({ value, onChange, options, placeholder, disabled, invalid, style, fontSize = 12, searchable, limit }) {
  const isSearchable = searchable ?? (options.length > 8);
  if (isSearchable) return <SearchSelect {...{ value, onChange, options, placeholder, disabled, invalid, style, fontSize, limit }} />;

  const sel = options.find(o => String(o.value) === String(value));
  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div style={{ position: 'relative', ...style }}>
        <ListboxButton
          className={`w-full flex items-center justify-between gap-2 rounded-[7px] bg-surface-2 border text-left transition-colors data-[hover]:border-border-3 data-[open]:border-accent disabled:opacity-50 ${invalid ? 'border-red' : 'border-border-2'}`}
          style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize }}>
          <span className={sel ? 'text-ink' : 'text-muted'} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {sel ? sel.label : (placeholder || 'Select…')}
          </span>
          <ChevronDown size={14} className="text-muted" style={{ flexShrink: 0 }} />
        </ListboxButton>
        <ListboxOptions anchor="bottom" transition
          className="z-[90] w-[var(--button-width)] [--anchor-gap:4px] !max-h-[200px] overflow-auto rounded-xl bg-surface border border-border-2 p-1 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.7)] focus:outline-none origin-top transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0">
          {options.map(o => (
            <ListboxOption key={String(o.value)} value={o.value}
              className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer select-none text-ink-2 data-[focus]:bg-white/[0.05] data-[focus]:text-ink data-[selected]:text-accent"
              style={{ fontFamily: 'monospace', fontSize }}>
              <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
              {String(o.value) === String(value) && <Check size={13} className="text-accent" style={{ flexShrink: 0 }} />}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
}

// Searchable variant (type to filter)
function SearchSelect({ value, onChange, options, placeholder, disabled, invalid, style, fontSize, limit }) {
  const [query, setQuery] = useState('');
  const display = v => { const o = options.find(x => String(x.value) === String(v)); return o ? o.label : ''; };
  const filtered = query.trim() === '' ? options : options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()));
  const shown = limit ? filtered.slice(0, limit) : filtered;
  return (
    <Combobox value={value} onChange={v => { onChange(v); setQuery(''); }} disabled={disabled}>
      <div style={{ position: 'relative', ...style }}>
        <div className={`flex items-center rounded-[7px] bg-surface-2 border transition-colors focus-within:border-accent ${invalid ? 'border-red' : 'border-border-2'}`}>
          <ComboboxInput
            className="w-full bg-transparent text-ink outline-none placeholder:text-muted"
            style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize }}
            displayValue={display}
            onChange={e => setQuery(e.target.value)}
            placeholder={placeholder || 'Search…'} />
          <ComboboxButton className="px-2 flex-shrink-0"><ChevronDown size={14} className="text-muted" /></ComboboxButton>
        </div>
        <ComboboxOptions anchor="bottom" transition
          className="z-[90] w-[var(--input-width)] [--anchor-gap:4px] !max-h-[200px] overflow-auto rounded-xl bg-surface border border-border-2 p-1 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.7)] focus:outline-none origin-top transition duration-150 ease-out data-[closed]:scale-95 data-[closed]:opacity-0 empty:hidden">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-muted" style={{ fontFamily: 'monospace', fontSize }}>No matches</div>
          ) : (<>
            {shown.map(o => (
              <ComboboxOption key={String(o.value)} value={o.value}
                className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg cursor-pointer select-none text-ink-2 data-[focus]:bg-white/[0.05] data-[focus]:text-ink data-[selected]:text-accent"
                style={{ fontFamily: 'monospace', fontSize }}>
                <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{o.label}</span>
                {String(o.value) === String(value) && <Check size={13} className="text-accent" style={{ flexShrink: 0 }} />}
              </ComboboxOption>
            ))}
            {filtered.length > shown.length && (
              <div className="px-3 py-1.5 text-muted-2" style={{ fontFamily: 'monospace', fontSize: fontSize - 1 }}>+{filtered.length - shown.length} more - keep typing…</div>
            )}
          </>)}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
}
